import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { CarouselFilters } from '@/components/projects/carousel/filters/CarouselFilterProps';
import CourseTypeFilter, {
  CourseTypeFilterValue,
} from '@/components/projects/carousel/filters/CourseTypeFilter';
import HackathonBannerInformation from '@/components/projects/hackathon/HackathonBanner/HackathonBannerInformation';
import { ProjectFilters } from '@/data/services/projectServices';
import Hackathon from '@/models/Hackathon';
import PodiumAll from './PodiumAll';
import PodiumByCourse from './PodiumByCourse';
import useHackathon from '@/data/hook/hackathon/useHackathon';
import useAuth from '@/data/hook/useAuth';
import HackathonStagesPermission from '@/utils/permissions/HackathonStagesPermission';
import Skeleton from '@/components/ui/custom/Skeleton';
import { useTranslation } from 'react-i18next';

type HackathonRankingProps = {
  hackathon: Hackathon;
  onFilter: (filter: ProjectFilters) => void;
  filter: ProjectFilters;
};

export default function HackathonPodium({
  hackathon,
  filter,
  onFilter,
}: HackathonRankingProps) {
  const adapterValue = ({
    courseType,
  }: CarouselFilters): CourseTypeFilterValue => {
    return courseType?.at(0) || 'all';
  };

  const courseType = adapterValue(filter);

  return (
    <div className="outline-offset-4 outline outline-1 outline-hackathon-primary rounded-lg border border-hackathon-secondary flex flex-col md:gap-1 gap-3 p-3 items-center justify-between mt-3 bg-hackathon-primary/10">
      <div className="flex justify-between w-full md:items-center items-start">
        <Text
          format="rubik-500"
          size="text-20"
          text={`Ranking`}
          className="text-hackathon-primary"
        />
        <div className="flex gap-2 md:items-center items-end md:flex-row flex-col ">
          <Text
            format="rubik-500"
            size="text-16"
            text="Categorias"
            className="text-primary"
          />
          <CourseTypeFilter size="small" filter={filter} onFilter={onFilter} />
        </div>
      </div>
      <HackathonBannerInformation hackathon={hackathon} resume />
      <ConditionalRenderer condition={courseType === 'all'}>
        <PodiumAll hackathon={hackathon} />
      </ConditionalRenderer>
      <ConditionalRenderer condition={courseType !== 'all'}>
        <PodiumByCourse hackathon={hackathon} courseType={courseType} />
      </ConditionalRenderer>
      <RecordFinalLivestream />
    </div>
  );
}

const RecordFinalLivestream = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'hackathon.banner',
  });
  const { hackathon, hackathonsStages, isLoading } = useHackathon();
  const { user } = useAuth();
  const hackathonStagesPermission = new HackathonStagesPermission({
    model: hackathonsStages,
    user,
  });

  if (isLoading) return <Skeleton className="w-[200px] bg-primary-content" />;

  if (!hackathon?.liveStreamUrl) return null;

  return (
    <ConditionalRenderer
      condition={hackathonStagesPermission.canShowRecordLivestream()}
    >
      <div className="inline sm:flex gap-1 items-center w-full">
        <Text
          text={t('recordLiveStream', {
            edition: hackathon.edition,
          })}
          className="text-14"
        />
        <Text
          text={t('liveStreamLink')}
          href={hackathon.liveStreamUrl}
          className="text-14 underline text-primary"
        />
      </div>
    </ConditionalRenderer>
  );
};
